import {
  clone,
  compact,
  filter,
  find,
  get,
  isEmpty,
  isObject,
  orderBy,
  reject,
  some
} from 'lodash';
import dayjs from 'dayjs';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { SortOrder } from '/@/modules/core/app/constants/SortOrder';
import BooleanCellRender from '/@/modules/core/table/models/BooleanCellRender';
import CustomCellRender from '/@/modules/core/table/models/CustomCellRender';
import DocumentsCellRender from '/@/modules/core/table/models/DocumentsCellRender';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { commits } from '/@/modules/core/app/helpers/store';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import Lang from '/@/modules/core/app/services/Lang';
import Logger from '@/modules/core/app/helpers/Logger';

export class TableRenderService {
  static SEARCH_DEPTH_LEVEL = 2;

  /**
   * @var {string}
   */
  tableId;

  /**
   * @param {array}
   */
  originalData = [];

  /**
   * @var {string}
   */
  primaryKeyField;

  /**
   * @var {string}
   */
  primaryNameField;

  /**
   * @param tableId
   */
  setTableId(tableId) {
    this.tableId = tableId;
  }

  /**
   * @override
   *
   * @param data
   * @param columns
   * @return {Array<*>}
   */
  postProcessPayload(data, columns) {
    columns.forEach((column) => {
      this.setColumnProperties(column);
      data.forEach((row) => {
        this.formatCell(column, row);
      });
    });

    // Store data to be restored after non-async search
    this.originalData = clone(data);

    return [data, columns];
  }

  /**
   * @param data
   * @param columns
   * @param row
   * @return {Array<*>}
   */
  postProcessAddRow(data, columns, row) {
    this.formatRow(row, columns);
    data.push(row);
    this.originalData.push(row);
    return data;
  }

  /**
   * @param data
   * @param columns
   * @param row
   * @param newRow
   * @return {Array<*>}
   */
  postProcessUpdateRow(data, columns, row, newRow) {
    if (isEmpty(newRow)) {
      return this.postProcessDeleteRow(data, row);
    }
    this.formatRow(newRow, columns);
    data = this.swapItem(data, newRow);
    this.originalData = this.swapItem(this.originalData, newRow);
    return data;
  }

  /**
   * @param data
   * @param row
   * @return {Array<*>}
   */
  postProcessDeleteRow(data, row) {
    data = this.rejectItem(data, row);
    this.originalData = this.rejectItem(this.originalData, row);
    return data;
  }

  /**
   * @param columns
   * @returns {BaseColumns[]}
   */
  preProcessColumns(columns) {
    return compact(orderBy(columns, 'order'));
  }

  /**
   * @param query
   * @param columns
   * @returns {[]|*}
   */
  searchData(query, columns) {
    try {
      const regQuery = new RegExp(query, 'i');

      return filter(this.originalData, (row) => {
        try {
          return this.searchDataByValue(row[this.getPrimaryNameField(columns)], regQuery, 1);
        } catch (e) {
          Logger.error(`Error in searchData for row ${row}: ${e.message}`);

          return false;
        }
      });
    } catch {
      return [];
    }
  }

  searchDataByValue(value, regQuery, depth) {
    if (typeof value === 'string') {
      return value.match(regQuery);
    }
    if (depth > TableRenderService.SEARCH_DEPTH_LEVEL) {
      return false;
    }
    if (isObject(value)) {
      return some(value, (item) => this.searchDataByValue(item, regQuery, depth + 1));
    }
  }

  /**
   * @returns {Array}
   */
  getOriginalData() {
    return this.originalData;
  }

  /**
   * This method is supposed to be overridden with switch case in body returning customSortFn for every column
   * @param {TableColumn} column
   * @returns {null|Function}
   */
  getCustomSortFn(column) {
    return column.customSort ?? null;
  }

  /**
   * This is used for setting the pagination sent to the backend
   * @param page
   * @returns {number}
   */
  getPage(page) {
    return page - 1;
  }

  /**
   * @param order
   * @param field
   * @returns {string}
   */
  getSortField(order, field) {
    return order === SortOrder.DESC ? `-${field}` : field;
  }

  /**
   *
   * @param columns
   * @returns {string|null}
   */
  getDefaultSort(columns) {
    return get(find(columns, { defaultSort: true }), 'field');
  }

  /**
   * @param {TableColumn[]} columns
   * @returns {string}
   */
  getPrimaryKeyField(columns) {
    if (this.primaryKeyField) {
      return this.primaryKeyField;
    }
    this.primaryKeyField = get(find(columns, { isPrimaryKey: true }), 'field');
    return this.primaryKeyField;
  }

  /**
   * @param {TableColumn[]} columns
   * @returns {string}
   */
  getPrimaryNameField(columns) {
    if (this.primaryNameField) {
      return this.primaryNameField;
    }
    this.primaryNameField = get(find(columns, { isPrimaryName: true }), 'searchField');
    return this.primaryNameField;
  }

  /**
   * @param field
   * @returns {boolean}
   */
  isDocumentsField(field) {
    return field === 'documents';
  }

  /**
   * @param item
   */
  updateItem(item) {
    const { tableId } = this;
    commits.table.updateItem({ tableId, item });
  }

  /**
   * @param item
   */
  copyItem(item) {
    const { tableId } = this;
    commits.table.addItem({ tableId, item });
  }

  /**
   * @param itemId
   */
  removeItem(itemId) {
    const { tableId } = this;
    // commits.table.deleteItem({ tableId, itemId });
  }

  /**
   *
   * @param columns
   * @param dtData
   * @param selected
   * @returns {null|*}
   */
  getSelected(columns, dtData, selected) {
    if (!selected) {
      return null;
    }
    const primaryColumn = columns.find((column) => column.isPrimaryKey);
    return dtData.find((data) => selected === data[primaryColumn.field]);
  }

  /**
   * @param row
   * @param columns
   */
  formatRow(row, columns) {
    columns.forEach((column) => {
      this.formatCell(column, row);
    });
  }

  /**
   * @param {TableColumn} column
   * @param row
   */
  formatTagValue(column, row) {
    column.renderHtml = true;
    const { field } = column;
    if (row[field] && !row[column._field]) {
      const enumItem = column.values[row[field]];
      row[column._field] = column.visible
        ? `<span class="tag is-light has-text-weight-semibold" style="color:${enumItem.color}; background-color:${enumItem.color}1A !important">${enumItem.value}</span>`
        : enumItem;
    }
  }

  /**
   * @param {TableColumn} column
   * @private
   */
  setColumnProperties(column) {
    column.numeric = column.isNumeric;
    column.customSort = this.getCustomSortFn(column);
    column.meta.isCustomHeaderRender = column.isCustomHeaderRender;
    if (column.isCustomCellRender) {
      column.meta.cellRender = new CustomCellRender();
    }
  }

  /**
   * @param {TableColumn} column
   * @param row
   * @private
   */
  formatCell(column, row) {
    if (column.isCustomCellRender) {
      return;
    }

    if (column.isPrimaryKey) {
      return;
    }

    column.currencySymbol = row.currency?.symbol || null;

    if (column.customRenderFn) {
      this.formatCustomRenderFn(column, row);
      return;
    }

    if (this.isDocumentsField(column.field)) {
      this.formatDocumentsCell(column);
      return;
    }

    if (column.isNumeric) {
      this.formatNumericalCell(column, row);
      return;
    }

    switch (column.format) {
      case ColumnFormat.FORMAT_BOOLEAN:
        this.formatBooleanCell(column);
        break;

      case ColumnFormat.FORMAT_CUSTOM:
        break;

      case ColumnFormat.FORMAT_DATE:
        this.formatDateCell(column, row);
        break;

      case ColumnFormat.FORMAT_DATETIME:
        this.formatDateTimeCell(column, row);
        break;

      case ColumnFormat.FORMAT_ENUM:
        this.formatTagValue(column, row);
        break;

      default:
        break;
    }
  }

  /**
   * @param {TableColumn} column
   * @param row
   * @private
   */
  formatCustomRenderFn(column, row) {
    row[column._field] = column.customRenderFn(row);
  }

  /**
   * @param {TableColumn} column
   * @private
   */
  formatDocumentsCell(column) {
    column.meta.cellRender = new DocumentsCellRender();
  }

  /**
   * @param {TableColumn} column
   * @private
   */
  formatBooleanCell(column) {
    column.centered = true;
    column.values = [
      { key: true, value: Lang.t('core.true') },
      { key: false, value: Lang.t('core.false') }
    ];
    column.meta.cellRender = new BooleanCellRender();
  }

  /**
   * @param {TableColumn} column
   * @param row
   * @private
   */
  formatNumericalCell(column, row) {
    const { field } = column;
    row[column._field] = NumeralService.formatColumnValue(row[column._field] ?? row[field], column);
  }

  /**
   * @param {TableColumn} column
   * @param row
   * @private
   */
  formatDateCell(column, row) {
    const { field } = column;
    row[column._field] = dayjs(row[column._field] ?? row[field]).format(DateFormat.ISO);
  }

  /**
   * @param {TableColumn} column
   * @param row
   * @private
   */
  formatDateTimeCell(column, row) {
    const { field } = column;
    row[column._field] = dayjs(row[column._field] ?? row[field]).format(DateFormat.ISO_TIME);
  }

  /**
   * @param {*[]} data
   * @param {object} row
   * @return {*[]}
   */
  rejectItem(data, row) {
    return reject(data, (item) => item[this.primaryKeyField] === row[this.primaryKeyField]);
  }

  /**
   * @param {*[]} data
   * @param {object} row
   * @return {*[]}
   */
  swapItem(data, row) {
    return data.map((item) =>
      item[this.primaryKeyField] === row[this.primaryKeyField] ? row : item
    );
  }
}
